import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Typography, Button } from "antd";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { AdminDashboardAnalytics } from "@components";
import DashboardSpaceBackground from "./DashboardSpaceBackground";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { getAdminAnalyticsAction } from "@redux/actions/adminActions";
import { useTranslation } from "react-i18next";
import { languages } from "@utils/languages";
import { getValue, setValue } from "@utils/localStorage";
import OnboardingModal from "../../onboard/OnboardingModal";
import SubscriptionModal from "../../onboard/SubscriptionModal";
import {
  FaUserTie,
  FaStethoscope,
  FaCalculator,
  FaCode,
  FaBullhorn,
  FaHome,
  FaGraduationCap,
  FaPen,
} from "react-icons/fa";

const { Text } = Typography;

// Local storage keys for modal completion
const ONBOARDING_COMPLETE_KEY = "copilotly_onboarding_complete";
const SUBSCRIPTION_COMPLETE_KEY = "copilotly_subscription_complete";

const Dashboard = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state?.auth);
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();

  // Language switcher state
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const [langSearchQuery, setLangSearchQuery] = useState("");
  const langButtonRef = useRef(null);
  const langSearchRef = useRef(null);

  // Onboarding/Subscription modal state
  const isOnboardingCompleted =
    user?.onboarding_complete ||
    localStorage.getItem(ONBOARDING_COMPLETE_KEY) === "true";

  const isSubscriptionCompleted =
    user?.subscribe_complete ||
    user?.appSumoUser ||
    localStorage.getItem(SUBSCRIPTION_COMPLETE_KEY) === "true";

  const [onboardingModalVisible, setOnboardingModalVisible] = useState(
    !isOnboardingCompleted
  );

  const [subscriptionModalVisible, setSubscriptionModalVisible] = useState(
    isOnboardingCompleted && !isSubscriptionCompleted
  );

  // Determine if any modal is active (for blur effect)
  const isModalActive = onboardingModalVisible || subscriptionModalVisible;

  // Handle onboarding modal completion
  const handleOnboardingComplete = () => {
    setOnboardingModalVisible(false);
    localStorage.setItem(ONBOARDING_COMPLETE_KEY, "true");

    // Show subscription modal after onboarding if user isn't subscribed
    if (!isSubscriptionCompleted) {
      setSubscriptionModalVisible(true);
    }
  };

  // Handle subscription completion
  const handleSubscriptionComplete = () => {
    setSubscriptionModalVisible(false);
    localStorage.setItem(SUBSCRIPTION_COMPLETE_KEY, "true");
  };

  useEffect(() => {
    if (user) {
      dispatch(getAdminAnalyticsAction(setLoading));
    }
  }, [dispatch, user]);

  // Sync user state with localStorage
  useEffect(() => {
    if (
      user?.onboarding_complete &&
      localStorage.getItem(ONBOARDING_COMPLETE_KEY) !== "true"
    ) {
      localStorage.setItem(ONBOARDING_COMPLETE_KEY, "true");
    }

    if (
      (user?.subscribe_complete || user?.appSumoUser) &&
      localStorage.getItem(SUBSCRIPTION_COMPLETE_KEY) !== "true"
    ) {
      localStorage.setItem(SUBSCRIPTION_COMPLETE_KEY, "true");
    }
  }, [user]);

  // Fetch stored language
  useEffect(() => {
    const fetchLanguage = async () => {
      const storedLanguage = await getValue("language");
      if (storedLanguage) {
        setSelectedLanguage(storedLanguage);
      }
    };
    fetchLanguage();
  }, []);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setValue("language", lang);
    setSelectedLanguage(lang);
    setLangDropdownOpen(false);
    setLangSearchQuery("");
  };

  const handleLangButtonClick = () => {
    if (langButtonRef.current) {
      const rect = langButtonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setLangDropdownOpen(!langDropdownOpen);
    setLangSearchQuery("");
    setTimeout(() => {
      if (langSearchRef.current) {
        langSearchRef.current.focus();
      }
    }, 50);
  };

  const filteredLanguages = languages.filter((lang) => {
    const langName = t(lang.title).toLowerCase();
    const query = langSearchQuery.toLowerCase();
    return langName.includes(query) || lang.code.toLowerCase().includes(query);
  });

  const currentLang = languages.find(l => l.code === selectedLanguage);
  const langDropdownRef = useRef(null);

  // Close dropdown when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        langDropdownOpen &&
        langDropdownRef.current &&
        !langDropdownRef.current.contains(event.target) &&
        langButtonRef.current &&
        !langButtonRef.current.contains(event.target)
      ) {
        setLangDropdownOpen(false);
        setLangSearchQuery("");
      }
    };

    const handleScroll = () => {
      if (langDropdownOpen) {
        setLangDropdownOpen(false);
        setLangSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    // Listen for scroll on window and any scrollable containers
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [langDropdownOpen]);

  // AI Copilots available
  const copilots = [
    { icon: FaUserTie, color: "#3B82F6", bg: "#3B82F6", nameKey: "dashboard.expertLawyer" },
    { icon: FaStethoscope, color: "#10B981", bg: "#10B981", nameKey: "dashboard.expertDoctor" },
    { icon: FaCalculator, color: "#F59E0B", bg: "#F59E0B", nameKey: "dashboard.expertAccountant" },
    { icon: FaCode, color: "#8B5CF6", bg: "#8B5CF6", nameKey: "dashboard.expertEngineer" },
    { icon: FaBullhorn, color: "#EC4899", bg: "#EC4899", nameKey: "dashboard.expertMarketer" },
    { icon: FaHome, color: "#06B6D4", bg: "#06B6D4", nameKey: "dashboard.expertRealEstate" },
    { icon: FaGraduationCap, color: "#EF4444", bg: "#EF4444", nameKey: "dashboard.expertTutor" },
    { icon: FaPen, color: "#6366F1", bg: "#6366F1", nameKey: "dashboard.expertWriter" },
  ];

  // AI Providers
  const providers = [
    { name: "GPT-4", color: "#10A37F" },
    { name: "Claude", color: "#D97706" },
    { name: "Gemini", color: "#4285F4" },
    { name: "DeepSeek", color: "#0EA5E9" },
  ];

  return (
    <>
      {/* Onboarding Modal */}
      {onboardingModalVisible && (
        <OnboardingModal
          visible={onboardingModalVisible}
          onClose={() => {}} // Don't allow direct closing
          onComplete={handleOnboardingComplete}
          user={user}
        />
      )}

      {/* Subscription Modal - Only shown after onboarding or if onboarded but not subscribed */}
      {subscriptionModalVisible && (
        <SubscriptionModal
          visible={subscriptionModalVisible}
          onClose={() => {}} // Don't allow direct closing
          onComplete={handleSubscriptionComplete}
          user={user}
        />
      )}

      {/* Language Dropdown Portal */}
      {langDropdownOpen && (
          <div
            ref={langDropdownRef}
            style={{
              position: "fixed",
              top: `${dropdownPos.top}px`,
              right: `${dropdownPos.right}px`,
              background: "rgba(10, 6, 18, 0.95)",
              backdropFilter: "blur(20px)",
              borderRadius: "12px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
              border: "1px solid rgba(255,255,255,0.1)",
              padding: "8px",
              minWidth: "200px",
              maxWidth: "240px",
              zIndex: 9999,
            }}
          >
            <div style={{ padding: "8px 12px", marginBottom: "4px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
              <span style={{ fontSize: "11px", fontWeight: 700, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {t("dashboard.changeLanguage") || "Change Language"}
              </span>
            </div>
            <div style={{ padding: "4px 4px 8px 4px" }}>
              <input
                ref={langSearchRef}
                type="text"
                value={langSearchQuery}
                onChange={(e) => setLangSearchQuery(e.target.value)}
                placeholder={t("common.search") || "Search..."}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: "8px",
                  fontSize: "13px",
                  outline: "none",
                  background: "rgba(255,255,255,0.05)",
                  color: "#fff",
                }}
                onFocus={(e) => e.target.style.borderColor = "rgba(139, 92, 246, 0.5)"}
                onBlur={(e) => e.target.style.borderColor = "rgba(255,255,255,0.15)"}
              />
            </div>
            <div style={{ maxHeight: "280px", overflowY: "auto", paddingRight: "4px" }}>
              {filteredLanguages.length === 0 ? (
                <div style={{ padding: "12px", textAlign: "center", color: "rgba(255,255,255,0.5)", fontSize: "13px" }}>
                  {t("common.noResults") || "No languages found"}
                </div>
              ) : (
                filteredLanguages.map((lang) => {
                  const isSelected = selectedLanguage === lang.code;
                  return (
                    <div
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        background: isSelected ? "rgba(139, 92, 246, 0.2)" : "transparent",
                        transition: "all 0.15s ease",
                        marginTop: "2px",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = "transparent";
                        }
                      }}
                    >
                      <img
                        src={lang.img}
                        alt={lang.code}
                        style={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "4px",
                          objectFit: "cover",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                        }}
                      />
                      <span style={{
                        fontSize: "14px",
                        fontWeight: isSelected ? 600 : 500,
                        color: isSelected ? "#a78bfa" : "rgba(255,255,255,0.8)",
                        flex: 1,
                      }}>
                        {t(lang.title)}
                      </span>
                      {isSelected && (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M3 8L6 11L13 4" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
      )}

      <Row
        style={{
          height: "100vh",
          background: "#ffffff",
          filter: isModalActive ? "blur(3px)" : "none",
          transition: "filter 0.3s ease",
          pointerEvents: isModalActive ? "none" : "auto",
        }}
      >
      <Col
        span={24}
        style={{
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "24px",
        }}
      >
        <div style={{ position: "relative" }}>
          {/* Hero Section - Enhanced with Platform Capabilities */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ marginBottom: "24px" }}
          >
            <div
              style={{
                background: "#0a0612",
                borderRadius: "20px",
                padding: "32px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Space Background with shooting stars */}
              <DashboardSpaceBackground />

              <Row align="middle" justify="space-between" style={{ position: "relative", zIndex: 1 }}>
                <Col xs={24} lg={16}>
                  {/* Welcome */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                    <span style={{ fontSize: "28px" }}>👋</span>
                    <h1 style={{
                      color: "#fff",
                      fontSize: "26px",
                      fontWeight: 700,
                      margin: 0,
                      letterSpacing: "-0.5px"
                    }}>
                      {t("dashboard.welcomeBack")}, {user?.name?.split(" ")[0] || "there"}!
                    </h1>
                  </div>

                  {/* Description */}
                  <p
                    style={{
                      color: "rgba(255,255,255,0.95)",
                      fontSize: "15px",
                      margin: "0 0 16px 0",
                      maxWidth: "600px",
                      lineHeight: 1.7
                    }}
                    dangerouslySetInnerHTML={{ __html: t("dashboard.heroDescription") }}
                  />

                  {/* AI Copilots showcase */}
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "16px",
                    flexWrap: "wrap"
                  }}>
                    <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px" }}>
                      {t("dashboard.aiExperts")}
                    </span>
                    {copilots.map((copilot, idx) => {
                      const Icon = copilot.icon;
                      return (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "8px 12px",
                            background: "rgba(255,255,255,0.08)",
                            borderRadius: "10px",
                            border: "1px solid rgba(255,255,255,0.15)",
                            cursor: "pointer",
                            transition: "all 0.25s ease",
                            backdropFilter: "blur(12px)",
                            boxShadow: "0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.1)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = `rgba(255,255,255,0.15)`;
                            e.currentTarget.style.borderColor = `${copilot.color}60`;
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow = `0 8px 24px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.15), 0 0 20px ${copilot.color}30`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                            e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.1)";
                          }}
                        >
                          <Icon size={14} style={{ color: copilot.color }} />
                          <span style={{ fontSize: "11px", fontWeight: 500, color: "rgba(255,255,255,0.9)" }}>
                            {t(copilot.nameKey)}
                          </span>
                        </div>
                      );
                    })}
                    <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "11px", fontWeight: 500 }}>{t("dashboard.andMore")}</span>
                  </div>

                  {/* Platform capabilities pills */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "16px" }}>
                    {[
                      { emoji: "🤖", textKey: "dashboard.aiProfessionalExperts" },
                      { emoji: "🌐", textKey: "dashboard.worksEverywhere" },
                      { emoji: "💬", textKey: "dashboard.instantAdvice" },
                      { emoji: "🌍", textKey: "dashboard.languages24" },
                    ].map((item, i) => (
                      <div
                        key={i}
                        style={{
                          background: "rgba(167, 139, 250, 0.1)",
                          border: "1px solid rgba(167, 139, 250, 0.25)",
                          padding: "6px 12px",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          fontSize: "11px",
                          color: "rgba(255,255,255,0.85)",
                          fontWeight: 500,
                        }}
                      >
                        <span style={{ fontSize: "12px" }}>{item.emoji}</span> {t(item.textKey)}
                      </div>
                    ))}
                  </div>

                  {/* AI Providers */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "11px", fontWeight: 500 }}>{t("dashboard.poweredBy")}</span>
                    {providers.map((provider, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: "rgba(255,255,255,0.08)",
                          padding: "4px 10px",
                          borderRadius: "8px",
                          fontSize: "11px",
                          color: "rgba(255,255,255,0.8)",
                          fontWeight: 600,
                          border: "1px solid rgba(255,255,255,0.12)",
                        }}
                      >
                        {provider.name}
                      </div>
                    ))}
                  </div>
                </Col>

                <Col xs={24} lg={8} style={{ textAlign: "right" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "flex-end", marginTop: "16px" }}>
                    {/* Stats badge */}
                    <div style={{
                      background: "rgba(255,255,255,0.08)",
                      backdropFilter: "blur(12px)",
                      borderRadius: "12px",
                      padding: "12px 16px",
                      border: "1px solid rgba(255,255,255,0.15)",
                      textAlign: "center",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.1)",
                    }}>
                      <div style={{ color: "#fff", fontSize: "20px", fontWeight: 700 }}>{t("dashboard.activeUsersCount")}</div>
                      <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "11px", fontWeight: 500 }}>{t("dashboard.activeUsers")}</div>
                    </div>

                    {/* Upgrade CTA */}
                    <Link to="/profile/plans">
                      <Button
                        type="primary"
                        icon={<Sparkles size={16} />}
                        style={{
                          background: "rgba(255,255,255,0.95)",
                          color: "#6d28d9",
                          border: "none",
                          height: "44px",
                          borderRadius: "10px",
                          fontWeight: 600,
                          fontSize: "14px",
                          paddingLeft: "20px",
                          paddingRight: "20px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {t("dashboard.getProOffer")}
                      </Button>
                    </Link>

                    {/* Trust badge */}
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      color: "rgba(255,255,255,0.9)",
                      fontSize: "12px",
                    }}>
                      <span>⭐</span>
                      <span style={{ fontWeight: 600 }}>{t("dashboard.rating")}</span>
                      <span style={{ color: "rgba(255,255,255,0.7)" }}>{t("dashboard.fromReviews")}</span>
                    </div>

                    {/* Language Switcher Button */}
                    <div
                      ref={langButtonRef}
                      onClick={handleLangButtonClick}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "rgba(255,255,255,0.08)",
                        backdropFilter: "blur(12px)",
                        padding: "10px 16px",
                        borderRadius: "10px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        border: "1px solid rgba(255,255,255,0.15)",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.1)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(255,255,255,0.12)";
                        e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                        e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      {currentLang && (
                        <img
                          src={currentLang.img}
                          alt={currentLang.code}
                          style={{
                            width: "20px",
                            height: "20px",
                            borderRadius: "4px",
                            objectFit: "cover",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                          }}
                        />
                      )}
                      <span style={{ color: "rgba(255,255,255,0.9)", fontSize: "13px", fontWeight: 600 }}>
                        {currentLang?.code?.toUpperCase() || "EN"}
                      </span>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M3 5L6 8L9 5" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </motion.div>

          {/* Main Content Area */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <AdminDashboardAnalytics />
          </motion.div>
        </div>
      </Col>
    </Row>
    </>
  );
};

export default Dashboard;
